/**
 * Date / comparison / establishment filters for dashboard KPIs.
 */

import * as React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/forms/Button';
import { SegmentedControl } from '@/components/forms/SegmentedControl';
import { DateRangeInput } from '@/components/forms/DateRangeInput';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { useEstablishmentsAutocomplete } from '@/hooks/useEstablishmentsAutocomplete';
import {
  SESSION_STATUS_CONFIG,
  type SessionStatusConfig,
} from '@/components/sessions/session-configs';
import type {
  DashboardStatsDateFilterState,
  DashboardStatsPeriod,
  ComparisonPeriod,
} from '@/utils/dashboard-stats-date-range-utils';
import { DATE_RANGE_PERIOD_OPTIONS_FULL } from '@/constants/shared-catalog';
import { KPI_COMPARISON_OPTIONS, KPI_FILTER_LABELS } from './kpi-configs';
import {
  applyKpiComparison,
  applyKpiCustomFrom,
  applyKpiCustomTo,
  applyKpiEstablishment,
  applyKpiPeriod,
  toKpiFilterDateString,
} from './kpi-utils';

/** Props for {@link KpiFilters}. */
export interface KpiFiltersProps {
  filters: DashboardStatsDateFilterState;
  onChange: (next: DashboardStatsDateFilterState) => void;
  disabled?: boolean;
  showPeriodFilter?: boolean;
  showDateRangeFilter?: boolean;
  showEstablishmentFilter?: boolean;
  showStatusFilter?: boolean;
  showComparisonFilter?: boolean;
  sessionStatuses?: string[];
  onToggleStatus?: (status: string) => void;
}

/** Date and scope filters shared by dashboard KPIs and charts. */
export const KpiFilters: React.FC<KpiFiltersProps> = ({
  filters,
  onChange,
  disabled,
  showPeriodFilter = false,
  showDateRangeFilter = false,
  showEstablishmentFilter = false,
  showStatusFilter = false,
  showComparisonFilter = false,
  sessionStatuses = [],
  onToggleStatus,
}) => {
  const usingCustomRange = Boolean(filters.customDateFrom || filters.customDateTo);
  const { options, isLoading, onSearch, onLoadMore, hasMore } = useEstablishmentsAutocomplete();
  const fromStr = toKpiFilterDateString(filters.customDateFrom);
  const toStr = toKpiFilterDateString(filters.customDateTo);

  function handlePeriodChange(value: string): void {
    onChange(applyKpiPeriod(value as DashboardStatsPeriod));
  }

  function handleFromChange(val: string): void {
    onChange(applyKpiCustomFrom(filters, val));
  }

  function handleToChange(val: string): void {
    onChange(applyKpiCustomTo(filters, val));
  }

  function handleComparisonChange(value: string): void {
    onChange(applyKpiComparison(value as ComparisonPeriod));
  }

  function handleEstablishmentChange(val: string): void {
    onChange(applyKpiEstablishment(filters, val));
  }

  /** Status-dot or lucide icon for one session-status chip. */
  function renderStatusIcon(config: SessionStatusConfig, isActive: boolean): React.ReactNode {
    if (config.isInProgress) {
      return (
        <span
          className="w-3 h-3 rounded-full shrink-0"
          style={{ background: isActive ? 'white' : config.badgeColor }}
        />
      );
    }
    const Icon = config.icon;
    return <Icon className="w-3.5 h-3.5" />;
  }

  /** One session-status chip in the status filter group. */
  function renderStatusButton([key, config]: [string, SessionStatusConfig]): React.ReactNode {
    const isActive = sessionStatuses.includes(key);

    function handleToggleStatusClick(): void {
      onToggleStatus?.(key);
    }

    return (
      <Button
        key={key}
        variant="custom"
        size="sm"
        className={cn(
          'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all border',
          isActive ? 'filter-badge-active' : 'filter-badge-inactive',
        )}
        style={{ '--filter-color': config.badgeColor } as React.CSSProperties}
        onClick={handleToggleStatusClick}
      >
        {renderStatusIcon(config, isActive)}
        {config.label}
      </Button>
    );
  }

  /** Preset period segmented control. */
  function renderPeriodFilter(): React.ReactNode {
    if (!showPeriodFilter) return null;
    return (
      <div className="filter-group">
        <span className="filter-group-label">{KPI_FILTER_LABELS.period}</span>
        <SegmentedControl
          size="sm"
          value={usingCustomRange ? '' : filters.dateRange}
          onChange={handlePeriodChange}
          options={[...DATE_RANGE_PERIOD_OPTIONS_FULL]}
        />
      </div>
    );
  }

  /** Custom from/to date range. */
  function renderDateRangeFilter(): React.ReactNode {
    if (!showDateRangeFilter) return null;
    return (
      <div className="filter-group">
        <span className="filter-group-label">{KPI_FILTER_LABELS.customDate}</span>
        <div className="w-auto mt-0.5">
          <DateRangeInput
            startDate={fromStr}
            endDate={toStr}
            onStartDateChange={handleFromChange}
            onEndDateChange={handleToChange}
            layout="horizontal"
            showConnector={true}
            showPresets={false}
            hidePickerLabels={true}
            compact={true}
          />
        </div>
      </div>
    );
  }

  /** Comparison-period segmented control. */
  function renderComparisonFilter(): React.ReactNode {
    if (!showComparisonFilter) return null;
    return (
      <div className="filter-group">
        <span className="filter-group-label">{KPI_FILTER_LABELS.comparison}</span>
        <SegmentedControl
          size="sm"
          value={filters.comparisonPeriod}
          onChange={handleComparisonChange}
          options={KPI_COMPARISON_OPTIONS}
        />
      </div>
    );
  }

  /** Session-status chips (charts only). */
  function renderStatusFilter(): React.ReactNode {
    if (!showStatusFilter || !onToggleStatus) return null;
    return (
      <div className="filter-group">
        <span className="filter-group-label">{KPI_FILTER_LABELS.status}</span>
        <div className="filter-options">
          {Object.entries(SESSION_STATUS_CONFIG).map(renderStatusButton)}
        </div>
      </div>
    );
  }

  /** Single-establishment autocomplete. */
  function renderEstablishmentFilter(): React.ReactNode {
    if (!showEstablishmentFilter) return null;
    return (
      <div className="filter-group">
        <span className="filter-group-label">{KPI_FILTER_LABELS.establishment}</span>
        <div className="w-64">
          <Autocomplete
            placeholder={KPI_FILTER_LABELS.searchEstablishment}
            options={options}
            value={filters.establishmentIds?.[0] ?? ''}
            onChange={handleEstablishmentChange}
            onSearch={onSearch}
            onLoadMore={onLoadMore}
            hasMore={hasMore}
            isLoading={isLoading}
            minSearchLength={2}
            inputSize="sm"
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn('filter-groups-container mb-5', disabled && 'pointer-events-none opacity-60')}
    >
      {renderPeriodFilter()}
      {renderDateRangeFilter()}
      {renderComparisonFilter()}
      {renderStatusFilter()}
      {renderEstablishmentFilter()}
    </div>
  );
};
